import * as React from 'react';
import { useRealtimeSubscription } from '@klareospace/pharos-lib-front-utils';
import type { ImportCompletedPayload, PharosRealtimeNotification } from '@klareospace/pharos-lib-types';
import { REALTIME_USER_EVENTS } from '@klareospace/pharos-lib-types';
import { useQueryClient } from '@tanstack/react-query';

import { importKeys } from '../api';
import {
  dismissImportNotification,
  IMPORT_PENDING_DURATION_MS,
  resetImportNotifications,
  showImportCompletion,
  useVisibleImportNotification,
} from '../hooks';
import { ImportCompletionPopup } from './import-completion-popup';
import { ImportPendingPopup } from './import-pending-popup';

/**
 * Mounted once in `_authenticated.tsx` so the realtime subscription survives the post-upload
 * redirect. Reads the single-slot notification store: a completion structurally replaces a
 * pending card for the same import. Only the pending card auto-dismisses (after
 * `IMPORT_PENDING_DURATION_MS`); the completed card closes only via its X or an action link.
 */
export function ImportNotificationListener(): React.JSX.Element | null {
  const queryClient = useQueryClient();
  const visibleNotification = useVisibleImportNotification();
  const cardRef = React.useRef<HTMLDivElement>(null);

  function handleImportCompleted(notification: PharosRealtimeNotification<ImportCompletedPayload>): void {
    if (notification.type !== 'user') return;
    void queryClient.invalidateQueries({ queryKey: importKeys.all });
    showImportCompletion(notification.payload);
  }

  useRealtimeSubscription<ImportCompletedPayload>({
    event: REALTIME_USER_EVENTS.IMPORT_COMPLETED,
    handler: handleImportCompleted,
  });

  // Clears the shared store on unmount (sign-out) so the next signed-in account never sees a
  // previous account's card. Safe under StrictMode's dev-only phantom unmount: mount/unmount/mount
  // happen synchronously with no event in between, so nothing can populate the store during that
  // window for the cleanup to wrongly drop.
  React.useEffect(() => {
    return (): void => {
      resetImportNotifications();
    };
  }, []);

  React.useEffect(() => {
    if (visibleNotification?.kind !== 'pending') return undefined;

    const timer = setTimeout(dismissImportNotification, IMPORT_PENDING_DURATION_MS);
    return (): void => clearTimeout(timer);
  }, [visibleNotification]);

  // Any CTA click anywhere in the app (outside the card itself) dismisses the notification — the
  // card's own action links already call `onDismiss` themselves, so clicks inside it are ignored.
  React.useEffect(() => {
    if (!visibleNotification) return undefined;

    function handleDocumentClick(event: MouseEvent): void {
      const target = event.target as HTMLElement;
      if (cardRef.current?.contains(target)) return;
      if (target.closest('a, button, [role="button"], [role="link"]')) {
        dismissImportNotification();
      }
    }

    document.addEventListener('click', handleDocumentClick, { capture: true });
    return (): void => document.removeEventListener('click', handleDocumentClick, { capture: true });
  }, [visibleNotification]);

  if (!visibleNotification) return null;

  if (visibleNotification.kind === 'pending') {
    return (
      <div ref={cardRef}>
        <ImportPendingPopup onDismiss={dismissImportNotification} />
      </div>
    );
  }

  return (
    <div ref={cardRef}>
      <ImportCompletionPopup onDismiss={dismissImportNotification} payload={visibleNotification.payload} />
    </div>
  );
}
